import { PictureElement } from '@/pictureElement/Picture';
import { generatePositionStyle } from '@/utils/positioning';
import { rewriteImageSrc } from '@/utils/rewriteImageSrc';
import { generateTransitionCSSProps } from '@/utils/transition';
import classNames from 'classnames';
import { type CSSProperties, forwardRef, useMemo } from 'react';
import { Link } from '../link';
import { getWidthStyleVariables } from '../utils/sizing';
import * as styles from './Image.css';
import type { ImageElementProps } from './Image.types';

const IMAGE_PLACEHOLDER_URL = '/image-placeholder.svg';

// TODO: implement srcSet & preload optimization
export const ImageElement = forwardRef<HTMLDivElement, ImageElementProps>((props, ref) => {
  const { element, children, attributes, hideLink } = props;
  const {
    url,
    src,
    alt = '',
    width,
    mobileWidth,
    mobileIsFullWidth,
    isFullWidth,
    // height,
    aspectRatio,
    href,
    borderColor,
    borderWidth,
    borderStyle,
    borderRadius,
    boxShadow,
    internal_link,
    open_in_new_tab,
    objectFit,
    padding,
    customAttributes,
    containerTransitions = [],
    imageTransitions = [],
  } = element.attrs || {};

  // Swap image src with a CF (CDN) one
  const imgSrc = useMemo(() => {
    const evaluatedSrc = src || url;

    if (!evaluatedSrc) {
      return IMAGE_PLACEHOLDER_URL;
    }

    return rewriteImageSrc(evaluatedSrc);
  }, [src, url]);

  const image = (
    <PictureElement src={imgSrc}>
      <img
        className={classNames(styles.img)}
        src={imgSrc}
        alt={alt}
        style={
          {
            '--object-fit': objectFit || 'contain',
            boxShadow: boxShadow,
            borderColor: borderColor || 'transparent',
            borderWidth: borderWidth,
            borderStyle: borderStyle,
            borderRadius: borderRadius,
            padding: padding,
            aspectRatio: aspectRatio,
            ...generateTransitionCSSProps(imageTransitions, 'hover-image'),
          } as CSSProperties
        }
      />
    </PictureElement>
  );

  const mobileHeight = aspectRatio && mobileWidth ? `${mobileWidth / aspectRatio}px` : 'auto';

  // compute height dynamically based on width and aspect ratio
  const height = useMemo(
    () => (width && width !== '100%' && aspectRatio ? `${width / aspectRatio}px` : 'auto'),
    [width, aspectRatio],
  );

  const containerStylesVariables = useMemo(
    () =>
      ({
        '--max-height': isFullWidth ? 'auto' : height,
        '--mobile-max-height':
          mobileIsFullWidth || (mobileIsFullWidth === undefined && isFullWidth) ? 'auto' : mobileHeight || height,
        ...getWidthStyleVariables({
          width,
          width_max: isFullWidth ? '100%' : undefined,
          variableMap: { width: '--width', maxWidth: '--max-width' },
        }),
        ...getWidthStyleVariables({
          width: mobileWidth || width || '100px',
          width_max: mobileIsFullWidth || isFullWidth ? '100%' : undefined,
          variableMap: {
            width: '--mobile-width',
            maxWidth: '--mobile-max-width',
          },
        }),
        ...generateTransitionCSSProps(containerTransitions, 'hover-container'),
      }) as CSSProperties,
    [isFullWidth, width, height, mobileIsFullWidth, containerTransitions, mobileWidth, mobileHeight],
  );

  return (
    <div
      ref={ref}
      {...customAttributes}
      className={classNames(styles.imgContainer, customAttributes?.className)}
      style={{
        ...containerStylesVariables,
        ...generatePositionStyle(element.attrs),
        borderRadius: borderRadius,
      }}
      {...attributes}
    >
      {/* Avoid wrapping the image with a link inside the editor to prevent */}
      {/* users from accidentally navigating away. */}
      {href && !hideLink ? (
        <Link href={href} internal_link={internal_link} open_in_new_tab={open_in_new_tab} className={styles.link}>
          {image}
        </Link>
      ) : (
        image
      )}
      {children}
    </div>
  );
});
